import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LogIn, AlertCircle, ArrowLeft, MailCheck, KeyRound } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";
import { api, formatApiError } from "../lib/api";

const LOGO = "/logo-badge.png";

export default function Login() {
  usePageTitle("Employee Login", "Secure employee portal sign-in for the AV Contracting crew dashboard.");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (user && user !== false) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(from, { replace: true });
    else setError(res.error || "Login failed");
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#23282B] av-grid flex items-center justify-center px-5 py-10" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="text-white/70 hover:text-[#9CC4DB] inline-flex items-center gap-2 text-xs tracking-widest uppercase mb-6" data-testid="login-back-home">
          <ArrowLeft size={14}/> Back to Site
        </Link>
        <div className="bg-white border-t-4 border-[#9CC4DB]">
          <div className="p-8 md:p-10">
            <div className="flex items-center gap-4">
              <img src={LOGO} alt="Amazing View Contracting" className="h-16 w-auto rounded-[4px]"/>
              <div>
                <div className="font-display text-2xl uppercase tracking-wider text-[#23282B]">Employee Portal</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F5F80] font-bold">Authorized Crew Only</div>
              </div>
            </div>

            {!forgotMode ? (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
              <label className="block">
                <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Email</span>
                <input
                  data-testid="login-email"
                  type="email"
                  required autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 text-[#2C3539] font-medium"
                />
              </label>
              <label className="block">
                <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Password</span>
                <input
                  data-testid="login-password"
                  type="password"
                  required autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 text-[#2C3539] font-medium"
                />
              </label>

              {error && (
                <div data-testid="login-error" className="bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700 inline-flex items-start gap-2">
                  <AlertCircle size={18} className="mt-0.5"/> {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white font-display tracking-wider text-lg px-7 py-4 uppercase disabled:opacity-50"
              >
                <LogIn size={18}/> {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
            ) : (
            <div className="mt-8" data-testid="forgot-panel">
              {forgotSent ? (
                <div data-testid="forgot-sent" className="bg-green-50 border-l-4 border-green-600 px-4 py-4 text-sm text-green-800 flex items-start gap-2">
                  <MailCheck size={18} className="mt-0.5 shrink-0"/>
                  If that email has an account, a reset link is on its way. It expires in 30 minutes — check spam too.
                </div>
              ) : (
                <form onSubmit={submitForgot} className="space-y-5" data-testid="forgot-form">
                  <p className="text-sm text-[#2C3539]/70">Enter your login email and we'll send you a secure link to set a new password.</p>
                  <label className="block">
                    <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Email</span>
                    <input
                      data-testid="forgot-email"
                      type="email"
                      required autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 text-[#2C3539] font-medium"
                    />
                  </label>
                  {error && (
                    <div data-testid="forgot-error" className="bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700 inline-flex items-start gap-2">
                      <AlertCircle size={18} className="mt-0.5"/> {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white font-display tracking-wider text-lg px-7 py-4 uppercase disabled:opacity-50"
                  >
                    <KeyRound size={18}/> {loading ? "Sending…" : "Email Reset Link"}
                  </button>
                </form>
              )}
              <button
                type="button"
                onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }}
                data-testid="forgot-back-to-login"
                className="mt-5 text-xs tracking-[0.15em] uppercase font-bold text-[#2F5F80] hover:text-[#234B66]"
              >
                ← Back to Sign In
              </button>
            </div>
            )}
          </div>
          <div className="bg-[#F4F5F6] px-8 md:px-10 py-4 text-xs text-[#2C3539]/60 tracking-wide">
            Forgot your password?{" "}
            <button
              type="button"
              onClick={() => { setForgotMode(true); setError(""); }}
              data-testid="forgot-password-link"
              className="text-[#2F5F80] font-bold hover:underline"
            >
              Reset it by email
            </button>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
